import { PeriodNavigator } from "@/shared/components/PeriodNavigator";
import { AllSectionSelector } from "@/shared/selectors/AllSectionSelector";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	FormHelperText,
	Grid2,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { ConsumptionReportPeriod } from "../hooks/useConsumptionReportPeriod";
import {
	UTILITY_CONSUMPTION_DIRECTION,
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionReportMode,
} from "../models/UtilityConsumption";
import {
	getUtilityConsumptionReportDate,
	getUtilityConsumptionReportPeriodLabel,
} from "../utils/calendar";

const moment = getMomentTz();
const REPORT_MODE_ORDER = Object.values(UTILITY_CONSUMPTION_REPORT_MODE);
const reportModeLabels: Record<UtilityConsumptionReportMode, string> = {
	day: "Day",
	week: "Week",
	month: "Month",
	year: "Year",
	period: "Period",
	section: "Section",
};

const calendarViews: Partial<
	Record<UtilityConsumptionReportMode, ("year" | "month" | "day")[]>
> = {
	year: ["year"],
	month: ["year", "month"],
};

interface ConsumptionReportControlsProps {
	period: ConsumptionReportPeriod;
	showSection?: boolean;
	sectionId: number | null;
	millNames: string[];
	nextDisabled: boolean;
	dateError: string | null;
	onSectionChange: (sectionId: number | null) => void;
}

export const ConsumptionReportControls = ({
	period,
	showSection = true,
	sectionId,
	millNames,
	nextDisabled,
	dateError,
	onSectionChange,
}: ConsumptionReportControlsProps) => {
	const { mode, isSection, calendarMode, activeRange } = period;
	const modes = showSection
		? REPORT_MODE_ORDER
		: REPORT_MODE_ORDER.filter(
				(value) => value !== UTILITY_CONSUMPTION_REPORT_MODE.SECTION,
			);
	const maxDate = isSection ? moment() : undefined;

	return (
		<>
			<Grid2 size={{ xs: 12, lg: "auto" }}>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={mode}
					aria-label="Consumption report mode"
					onChange={(_, value: UtilityConsumptionReportMode | null) =>
						value && period.changeMode(value)
					}
				>
					{modes.map((value) => (
						<ToggleButton key={value} value={value}>
							{reportModeLabels[value]}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			</Grid2>

			{calendarMode === null ? (
				<>
					<Grid2 size={{ xs: 12, sm: 4, lg: "auto" }}>
						<DatePicker
							label="Start"
							maxDate={maxDate}
							value={getUtilityConsumptionReportDate(activeRange.startDateKey)}
							onChange={(value) => period.setRangeDate("startDateKey", value)}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 4, lg: "auto" }}>
						<DatePicker
							label="End"
							maxDate={maxDate}
							value={getUtilityConsumptionReportDate(activeRange.endDateKey)}
							onChange={(value) => period.setRangeDate("endDateKey", value)}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
						{dateError && <FormHelperText error>{dateError}</FormHelperText>}
					</Grid2>
				</>
			) : (
				<Grid2 size={{ xs: 12, lg: "auto" }}>
					<PeriodNavigator
						label={getUtilityConsumptionReportPeriodLabel(
							calendarMode,
							period.referenceDate,
						)}
						value={moment(period.referenceDate)}
						views={calendarViews[mode] ?? ["year", "month", "day"]}
						maxDate={moment()}
						nextDisabled={nextDisabled}
						onPrevious={() =>
							period.stepCalendar(UTILITY_CONSUMPTION_DIRECTION.PREVIOUS)
						}
						onNext={() =>
							period.stepCalendar(UTILITY_CONSUMPTION_DIRECTION.NEXT)
						}
						onCurrent={() => period.stepCalendar()}
						onChange={period.setReferenceDate}
					/>
				</Grid2>
			)}

			{isSection && (
				<Grid2 size={{ xs: 12, sm: 4, lg: "auto" }}>
					<AllSectionSelector
						value={sectionId}
						onChange={onSectionChange}
						millNames={millNames}
						allOptionLabel="All"
					/>
				</Grid2>
			)}
		</>
	);
};
